'use client'

import { Fragment, useEffect, useState } from 'react'
import { usePage } from '@inertiajs/react'
import axios from 'axios'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/react'
import { Bars3Icon, MagnifyingGlassIcon, ShoppingBagIcon, XMarkIcon } from '@heroicons/react/24/outline'

export default function Header({ bannerMessage, navigation, cartCount: propCartCount = 0 }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const { cartCount = propCartCount, flash = {} } = usePage().props
  const safeNavigation = navigation ?? {}
  const navigationCategories = Array.isArray(safeNavigation.categories) ? safeNavigation.categories : []
  const navigationPages = Array.isArray(safeNavigation.pages) ? safeNavigation.pages : []

  useEffect(() => {
    if (searchTerm.trim().length < 2) { setSuggestions([]); return }
    const timer = setTimeout(() => axios.get(route('public.search.suggestions'), { params: { q: searchTerm } }).then(({ data }) => setSuggestions(data)).catch(() => setSuggestions([])), 250)
    return () => clearTimeout(timer)
  }, [searchTerm])

  return (
    <div className="bg-white sticky top-0 z-50 shadow-sm">
      {(flash.success || flash.error) && (
        <div role="status" className={`px-4 py-3 text-center text-sm font-semibold ${flash.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {flash.error || flash.success}
        </div>
      )}
      {/* Announcement Banner — hidden until shipping is available */}
      {/* {bannerMessage && (
        <div className="bg-[#222222] px-4 py-2 text-center text-xs font-medium text-white tracking-widest uppercase">
          {bannerMessage}
        </div>
      )} */}

      {/* Mobile Menu Dialog */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-linear data-closed:opacity-0"
        />
        <div className="fixed inset-0 z-50 flex">
          <DialogPanel
            transition
            className="relative flex w-full max-w-xs transform flex-col overflow-y-auto bg-white pb-12 shadow-xl transition duration-300 ease-in-out data-closed:-translate-x-full"
          >
            <div className="flex items-center justify-between px-4 pt-5 pb-4 border-b border-gray-100">
              <span className="text-lg font-black tracking-tight text-[#222222] uppercase">
                ANTONET<span className="text-[#F93A00]">SHOP</span>
              </span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            <TabGroup className="mt-2">
              <div className="border-b border-gray-200">
                <TabList className="-mb-px flex space-x-8 px-4">
                  {navigationCategories.map((category) => (
                    <Tab
                      key={category.name}
                      className="flex-1 border-b-2 border-transparent px-1 py-4 text-sm font-bold uppercase tracking-wider text-gray-600 data-selected:border-[#F93A00] data-selected:text-[#F93A00]"
                    >
                      {category.name}
                    </Tab>
                  ))}
                </TabList>
              </div>
              <TabPanels as={Fragment}>
                {navigationCategories.map((category) => (
                  <TabPanel key={category.name} className="space-y-8 px-4 pt-8 pb-8">
                    {category.sections.map((section) => (
                      <div key={section.name}>
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                          {section.name}
                        </p>
                        <ul role="list" className="space-y-3">
                          {section.items.map((item) => (
                            <li key={item.name}>
                              <a href={item.href} className="text-sm text-gray-700 hover:text-[#F93A00] transition-colors">
                                {item.name}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>

            <div className="space-y-4 border-t border-gray-200 px-4 py-6">
              {navigationPages.map((page) => (
                <div key={page.name}>
                  <a href={page.href} className="text-sm font-semibold uppercase tracking-wide text-gray-700 hover:text-[#F93A00] transition-colors">
                    {page.name}
                  </a>
                </div>
              ))}
            </div>

            <div className="space-y-4 border-t border-gray-200 px-4 py-6">
              <a href="/login" className="block text-sm font-bold uppercase tracking-wide text-gray-800 hover:text-[#F93A00] transition-colors">
                Sign In
              </a>
              <a href="/register" className="block text-sm font-bold uppercase tracking-wide text-gray-800 hover:text-[#F93A00] transition-colors">
                Register
              </a>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Main Navigation Bar */}
      <nav aria-label="Top" className="border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center">

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-gray-600 hover:text-[#F93A00] transition-colors lg:hidden"
            >
              <span className="sr-only">Open menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            {/* Logo */}
            <div className="flex lg:mr-10">
              <a href="/" className="flex items-center">
                <span className="sr-only">AntonetShop</span>
                <span className="text-xl font-black tracking-tight text-[#222222] uppercase">
                  ANTONET<span className="text-[#F93A00]">SHOP</span>
                </span>
              </a>
            </div>

            {/* Desktop flyout menus */}
            <PopoverGroup className="hidden lg:flex lg:items-center lg:gap-x-8 lg:h-full">
              {navigationCategories.map((category) => (
                <Popover key={category.name} className="flex h-full items-center">
                  <PopoverButton className="flex h-full items-center border-b-2 border-transparent text-sm font-bold uppercase tracking-wider text-gray-700 hover:text-[#F93A00] transition-colors data-open:border-[#F93A00] data-open:text-[#F93A00] focus:outline-none">
                    {category.name}
                  </PopoverButton>

                  <PopoverPanel
                    transition
                    className="absolute inset-x-0 top-full z-10 bg-white border-t border-gray-100 shadow-lg transition data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in max-h-[calc(100vh-3.5rem)] overflow-y-auto"
                  >
                    <div className="mx-auto max-w-7xl px-8 py-6">
                      <div className={`grid gap-x-8 gap-y-6 ${
                        category.sections.length <= 2 ? 'grid-cols-2' :
                        category.sections.length === 3 ? 'grid-cols-3' :
                        'grid-cols-4'
                      }`}>
                        {category.sections.map((section) => (
                          <div key={section.name}>
                            {section.name && (
                              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                                {section.name}
                              </p>
                            )}
                            <ul role="list" className="space-y-2">
                              {section.items.map((item) => (
                                <li key={item.name}>
                                  <a href={item.href} className="text-sm text-gray-600 hover:text-[#F93A00] transition-colors">
                                    {item.name}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </PopoverPanel>
                </Popover>
              ))}

                {navigationPages.map((page) => (
                <a
                  key={page.name}
                  href={page.href}
                  className="flex h-full items-center border-b-2 border-transparent text-sm font-bold uppercase tracking-wider text-gray-700 hover:text-[#F93A00] transition-colors"
                >
                  {page.name}
                </a>
              ))}
            </PopoverGroup>

            {/* Right-side icons */}
            <div className="ml-auto flex items-center gap-x-5">
              <div className="hidden lg:flex lg:items-center lg:gap-x-5">
                <a href="/login" className="text-xs font-bold uppercase tracking-wider text-gray-700 hover:text-[#F93A00] transition-colors">
                  Sign In
                </a>
                <span className="h-4 w-px bg-gray-300" aria-hidden="true" />
                <a href="/register" className="text-xs font-bold uppercase tracking-wider text-gray-700 hover:text-[#F93A00] transition-colors">
                  Register
                </a>
              </div>

              <div className="relative">
                <button type="button" onClick={() => setSearchOpen((open) => !open)} className="p-1.5 text-gray-600 hover:text-[#F93A00] transition-colors">
                  <span className="sr-only">Search</span><MagnifyingGlassIcon aria-hidden="true" className="size-5" />
                </button>
                {searchOpen && <form onSubmit={(event) => { event.preventDefault(); window.location.href = route('public.category') + '?search=' + encodeURIComponent(searchTerm) }} className="absolute right-0 top-10 z-50 w-72 bg-white p-3 shadow-xl ring-1 ring-gray-200">
                  <label htmlFor="header-search" className="sr-only">Search products</label><input id="header-search" autoFocus value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search products..." className="w-full border-gray-300 text-sm" />
                  {suggestions.length > 0 && <ul className="mt-2 border-t border-gray-100 pt-2">{suggestions.map((suggestion) => <li key={suggestion.slug || suggestion.name}><a href={route('public.productDetail', suggestion.slug || suggestion.name)} className="block py-1 text-sm text-gray-700 hover:text-[#F93A00]">{suggestion.name}</a></li>)}</ul>}
                </form>}
              </div>

              <a href={route('cart.index')} className="flex items-center gap-1.5 p-1.5 text-gray-600 hover:text-[#F93A00] transition-colors">
                <ShoppingBagIcon aria-hidden="true" className="size-5" />
                <span className="text-xs font-bold text-gray-700">{cartCount}</span>
                <span className="sr-only">items in cart</span>
              </a>
            </div>

          </div>
        </div>
      </nav>
    </div>
  )
}
